import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/ui/PageHeader';
import ProductCard from '@/components/ui/ProductCard';
import { PRODUCTS, CATEGORIES } from '@/data/products';

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get('cat') || 'all';
  const [sort, setSort] = useState('featured');

  const items = useMemo(() => {
    let list = activeCat === 'all' ? PRODUCTS : PRODUCTS.filter((p) => p.category === activeCat);
    if (sort === 'price-asc') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') list = [...list].sort((a, b) => b.price - a.price);
    if (sort === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [activeCat, sort]);

  return (
    <div data-testid="page-shop">
      <PageHeader
        eyebrow="The Shelf"
        title="Shop the"
        accent="full range."
        description="Every bottle, every flavor, every honest recipe — explore the complete Sip Theory shelf and find what speaks to your day."
      />

      {/* filter rail */}
      <section className="container-x pt-4 pb-10">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="shop-filters">
            <button
              onClick={() => setParams({})}
              className={`btn-pill-dark !text-[11px] ${activeCat === 'all' ? '!border-gold !text-gold' : ''}`}
              data-testid="filter-all"
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => setParams({ cat: c.slug })}
                className={`btn-pill-dark !text-[11px] ${activeCat === c.slug ? '!border-gold !text-gold' : ''}`}
                data-testid={`filter-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs tracking-[0.22em] uppercase text-bone-300/80">Sort</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-ink-800 border border-bone-100/12 text-bone-100 text-sm rounded-full px-4 py-2 outline-none focus:border-gold/60"
              data-testid="shop-sort"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low → High</option>
              <option value="price-desc">Price: High → Low</option>
              <option value="name">Name (A–Z)</option>
            </select>
          </div>
        </div>
      </section>

      <section className="container-x pb-24">
        {items.length === 0 ? (
          <div className="glass-card rounded-3xl p-12 text-center">
            <p className="text-bone-200">Nothing in this aisle yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
